export type ValidationContext = Record<string, unknown>;

export type ValidationRule = (
  value: unknown,
  formValues: ValidationContext
) => boolean | string | Promise<boolean | string>;

const toComparableDate = (value: string | Date | null | undefined): string | undefined => {
  if (!value) return undefined;

  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;

    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }

    return trimmed;
  }

  return undefined;
};

export const validateMinDate = (
  minDate: | string | Date | ((formValues: ValidationContext) => string | Date | undefined | null),
  message = 'Datum je před minimálním povoleným datem'
): ValidationRule => (value, formValues) => {
  if (!value) return true;

  const resolvedMinDate = typeof minDate === 'function' ? minDate(formValues) : minDate;
  const normalizedValue = toComparableDate(value as string | Date | null | undefined);
  const normalizedMinDate = toComparableDate(resolvedMinDate);

  if (!normalizedValue || !normalizedMinDate) return true;

  return normalizedValue >= normalizedMinDate || message;
};

export const validateMaxDate = (
  maxDate: | string | Date | ((formValues: ValidationContext) => string | Date | undefined | null),
  message = 'Datum je po maximálním povoleném datu'
): ValidationRule => (value, formValues) => {
  if (!value) return true;

  const resolvedMaxDate = typeof maxDate === 'function' ? maxDate(formValues) : maxDate;
  const normalizedValue = toComparableDate(value as string | Date | null | undefined);
  const normalizedMaxDate = toComparableDate(resolvedMaxDate);

  if (!normalizedValue || !normalizedMaxDate) return true;

  return normalizedValue <= normalizedMaxDate || message;
};

export const validateDateAfterOrEqual = (
  compareToField: string,
  message = 'Datum musí být stejné nebo pozdější'
): ValidationRule => (value, formValues) => {
  if (!value) return true;

  const currentValue = toComparableDate(value as string | Date | null | undefined);
  const compareValue = toComparableDate(
    formValues[compareToField] as string | Date | null | undefined
  );

  if (!currentValue || !compareValue) return true;

  return currentValue >= compareValue || message;
};

export const validateDateBeforeOrEqual = (
  compareToField: string,
  message = 'Datum musí být stejné nebo dřívější'
): ValidationRule => (value, formValues) => {
  if (!value) return true;

  const currentValue = toComparableDate(value as string | Date | null | undefined);
  const compareValue = toComparableDate(
    formValues[compareToField] as string | Date | null | undefined
  );

  if (!currentValue || !compareValue) return true;

  return currentValue <= compareValue || message;
};

export const notEqual = (
  compareToFields: string[],
  message = 'Nesmí být stejná hodnota'
): ValidationRule => (value, formValues) => {
  if (!value) return true;

  for (const field of compareToFields) {
    if (value === formValues[field]) {
      return message;
    }
  }

  return true;
};